import { useEffect, useState } from "react";
import { fetchTestimonials, createNote } from "@/lib/api";
import { toast } from "sonner";
import { formatTime } from "@/lib/utils";
import { CheckCircle2, Lock } from "lucide-react";

export default function NotesWindow() {
  const [tab, setTab] = useState("testimonials");
  const [testimonials, setTestimonials] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", message: "", is_private: false });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTestimonials().then(setTestimonials).catch(() => setTestimonials([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createNote(form);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send note");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#fffef8]" data-testid="notes-window-content">
      <div className="flex border-b border-black/10 bg-[#f7f5ef] shrink-0">
        {[["testimonials", "Testimonials"], ["write", "Write a note"]].map(([key, label]) => (
          <button
            key={key}
            data-testid={`notes-tab-${key}`}
            onClick={() => setTab(key)}
            className={`px-4 py-2.5 text-[13px] font-medium transition ${tab === key ? "text-gray-900 border-b-2 border-yellow-500 bg-white" : "text-gray-500 hover:text-gray-800"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "testimonials" && (
        <div className="flex-1 overflow-auto mac-scroll p-4 flex flex-col gap-3">
          {testimonials === null && <div className="text-sm text-gray-400">Loading…</div>}
          {testimonials?.length === 0 && (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-400 text-center px-6">
              No public testimonials yet — be the first to leave one.
            </div>
          )}
          {testimonials?.map((n) => (
            <div key={n.id} data-testid={`testimonial-${n.id}`} className="p-3 rounded-lg bg-yellow-50 border border-yellow-200/70 shadow-sm">
              <div className="text-[14px] text-gray-800 whitespace-pre-wrap">{n.message}</div>
              <div className="mt-2 text-[12px] text-gray-500">— {n.name} · {formatTime(n.created_at)}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "write" && (
        <div className="flex-1 overflow-auto mac-scroll p-4">
          {sent ? (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-center">
              <CheckCircle2 size={44} className="text-green-500" />
              <div className="font-semibold" data-testid="note-sent-title">Note sent</div>
              <div className="text-sm text-gray-600">
                {form.is_private
                  ? "Only Cory will see this."
                  : "Thanks! It'll show up publicly once Cory approves it."}
              </div>
              <button data-testid="note-write-another" className="text-sky-600 text-sm" onClick={() => { setSent(false); setForm({ name: "", email: "", message: "", is_private: false }); }}>
                Write another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-3" data-testid="note-form">
              <input data-testid="note-name" required placeholder="Your name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="px-3 py-2 rounded-lg border border-black/15 text-base md:text-sm outline-none focus:ring-2 focus:ring-yellow-300 bg-white" />
              <input data-testid="note-email" type="email" placeholder="Your email (optional)" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="px-3 py-2 rounded-lg border border-black/15 text-base md:text-sm outline-none focus:ring-2 focus:ring-yellow-300 bg-white" />
              <textarea data-testid="note-message" required rows={5} placeholder="Say something kind…" value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="px-3 py-2 rounded-lg border border-black/15 text-base md:text-sm outline-none focus:ring-2 focus:ring-yellow-300 resize-none bg-white" />
              <label className="flex items-center gap-2 text-[13px] text-gray-600 cursor-pointer">
                <input data-testid="note-private-toggle" type="checkbox" checked={form.is_private}
                  onChange={(e) => setForm({ ...form, is_private: e.target.checked })}
                  className="w-4 h-4 accent-yellow-500" />
                <Lock size={13} /> Private — only Cory sees this
              </label>
              <button data-testid="note-submit" disabled={submitting}
                className="mt-1 px-4 py-2.5 rounded-lg bg-yellow-400 text-gray-900 text-sm font-semibold hover:bg-yellow-500 transition disabled:opacity-50">
                {submitting ? "Sending…" : "Send note"}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
